import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Send, Mail, MapPin, Music } from 'lucide-react';

const Contact = () => {
  const { t, settings, API } = useApp();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/public/contact`, formData);
      toast.success('Message sent successfully!');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      console.error('Contact form error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-void">
      {/* Hero */}
      <section className="pt-32 pb-16 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="font-syne font-bold text-4xl md:text-5xl lg:text-6xl text-white mb-4 reveal" data-testid="contact-title">
            {t('contact.title', 'Contact')}
          </h1>
          <p className="text-white/50 text-lg reveal" style={{ animationDelay: '0.2s' }}>
            Let's create something amazing together
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="pb-24 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-8 reveal" style={{ animationDelay: '0.3s' }}>
              <div>
                <h2 className="font-syne font-semibold text-xl text-white mb-6">Get In Touch</h2>
                <p className="text-white/60 mb-8">
                  Whether you're interested in collaborating, booking, or just want to say hello – I'd love to hear from you.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-brand-red/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-brand-red" />
                  </div>
                  <div>
                    <h3 className="font-manrope font-medium text-white mb-1">Email</h3>
                    <p className="text-white/50 text-sm">contact@goldeng-music.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-brand-gold/20 flex items-center justify-center flex-shrink-0">
                    <Music className="w-5 h-5 text-brand-gold" />
                  </div>
                  <div>
                    <h3 className="font-manrope font-medium text-white mb-1">Bookings</h3>
                    <p className="text-white/50 text-sm">Available for shows and events</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-brand-red/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-brand-red" />
                  </div>
                  <div>
                    <h3 className="font-manrope font-medium text-white mb-1">Location</h3>
                    <p className="text-white/50 text-sm">Worldwide</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              {settings?.social_links && Object.keys(settings.social_links).length > 0 && (
                <div className="pt-6 border-t border-white/5">
                  <h3 className="font-manrope font-medium text-white mb-4">Follow Me</h3>
                  <div className="flex flex-wrap gap-3">
                    {settings.social_links.youtube && (
                      <a
                        href={settings.social_links.youtube}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-brand-dark border border-white/10 rounded-full text-white/70 hover:text-brand-red hover:border-brand-red transition-colors text-sm"
                      >
                        YouTube
                      </a>
                    )}
                    {settings.social_links.instagram && (
                      <a
                        href={settings.social_links.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-brand-dark border border-white/10 rounded-full text-white/70 hover:text-brand-red hover:border-brand-red transition-colors text-sm"
                      >
                        Instagram
                      </a>
                    )}
                    {settings.social_links.spotify && (
                      <a
                        href={settings.social_links.spotify}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-brand-dark border border-white/10 rounded-full text-white/70 hover:text-brand-red hover:border-brand-red transition-colors text-sm"
                      >
                        Spotify
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3 reveal" style={{ animationDelay: '0.4s' }}>
              <form onSubmit={handleSubmit} className="bg-brand-dark rounded-lg p-6 md:p-8 border border-white/5" data-testid="contact-form">
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-white/70">
                        {t('contact.name', 'Name')}
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="bg-brand-charcoal border-white/10 text-white focus:border-brand-red"
                        placeholder="Your name"
                        data-testid="contact-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-white/70">
                        {t('contact.email', 'Email')}
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="bg-brand-charcoal border-white/10 text-white focus:border-brand-red"
                        placeholder="your@email.com"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject" className="text-white/70">
                      {t('contact.subject', 'Subject')}
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="bg-brand-charcoal border-white/10 text-white focus:border-brand-red"
                      placeholder="What's this about?"
                      data-testid="contact-subject"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-white/70">
                      {t('contact.message', 'Message')}
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="bg-brand-charcoal border-white/10 text-white focus:border-brand-red resize-none"
                      placeholder="Your message..."
                      data-testid="contact-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full btn-primary"
                    data-testid="contact-submit"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        {t('contact.send', 'Send Message')}
                      </span>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
